import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import styled from 'styled-components';
import {
  LANGUAGE_LABELS,
  LANGUAGE_OPTIONS,
  type Language,
} from '@/admin/languages';
import { CONDITION_LABELS } from '@/admin/sitecontent/conditions';
import {
  parseBool,
  parseLanguage,
  tableSearchKeys,
} from '@/admin/sitecontent/listSearch';
import buttonStyles from '@/admin/styles/button.module.css';
import { Badge } from '@/admin/ui/Badge';
import { createTablePage } from '@/admin/ui/createTablePage';
import { Select } from '@/admin/ui/Form';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type Item = components['schemas']['OnboardingSequenceItem'];

const columnHelper = createColumnHelper<Item>();

const columns = [
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
    cell: (c) => c.getValue() || '—',
  }),
  columnHelper.accessor('language', {
    header: () => i18n.t('admin.common.language'),
    cell: (c) => i18n.t(LANGUAGE_LABELS[c.getValue()]),
  }),
  columnHelper.accessor((row) => row.activation_condition, {
    id: 'activation_condition',
    header: () => i18n.t('admin.onboardings.col.activationCondition'),
    cell: (c) => {
      const condition = c.getValue();
      if (!condition) return '—';
      const course = c.row.original.course_id;
      return course === null
        ? i18n.t(CONDITION_LABELS[condition])
        : `${i18n.t(CONDITION_LABELS[condition])} (#${course})`;
    },
  }),
  columnHelper.accessor('is_published', {
    header: () => i18n.t('admin.onboardings.col.published'),
    cell: (c) => (
      <Badge $variant={c.getValue() ? 'success' : 'neutral'}>
        {c.getValue() ? i18n.t('admin.common.yes') : i18n.t('admin.common.no')}
      </Badge>
    ),
  }),
  columnHelper.accessor('menu_order', {
    header: () => i18n.t('admin.onboardings.col.menuOrder'),
  }),
  columnHelper.accessor('slide_count', {
    header: () => i18n.t('admin.onboardings.col.slides'),
  }),
];

function Actions() {
  const navigate = useNavigate();
  const { language, published } = useSearch({ strict: false }) as {
    language?: Language;
    published?: boolean;
  };
  const set = (patch: Record<string, unknown>) =>
    void navigate({
      to: './',
      replace: true,
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        ...patch,
        page: undefined,
      })) as never,
    });
  return (
    <Bar>
      <Select
        aria-label={i18n.t('admin.common.language')}
        value={language ?? ''}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          set({ language: e.target.value || undefined })
        }
      >
        <option value="">{i18n.t('admin.help.allLanguages')}</option>
        {LANGUAGE_OPTIONS.map(([code, label]) => (
          <option key={code} value={code}>
            {i18n.t(label)}
          </option>
        ))}
      </Select>
      <Select
        aria-label={i18n.t('admin.onboardings.col.published')}
        value={published === undefined ? '' : String(published)}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          set({ published: e.target.value || undefined })
        }
      >
        <option value="">{i18n.t('admin.onboardings.allPublished')}</option>
        <option value="true">{i18n.t('admin.help.statusPublished')}</option>
        <option value="false">{i18n.t('admin.help.statusDraft')}</option>
      </Select>
      <Link to="/admin/onboardings/new/" className={buttonStyles.Button}>
        {i18n.t('admin.onboardings.newOnboarding')}
      </Link>
    </Bar>
  );
}

const OnboardingList = createTablePage<
  Item,
  '/admin/onboardings/$onboardingId'
>({
  name: 'admin.nav.onboardings',
  urlState: true,
  useData: (_page, _search, { paging }) => {
    const { language, published } = useSearch({ strict: false }) as {
      language?: Language;
      published?: boolean;
    };
    return api.listOnboardingSequences.useQuery({
      query: { ...paging, language, is_published: published },
    });
  },
  columns,
  itemBaseUrl: '/admin/onboardings/$onboardingId',
  getParams: (row) => ({ onboardingId: String(row.id) }),
  actions: <Actions />,
});

export const Route = createFileRoute('/admin/onboardings/')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: (
    s: Record<string, unknown>,
  ): { language?: Language; published?: boolean } & Record<
    string,
    unknown
  > => ({
    ...tableSearchKeys(s),
    language: parseLanguage(s.language),
    published: parseBool(s.published),
  }),
  component: OnboardingList,
});

const Bar = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;
